import React, { useState, useEffect } from 'react';
import { CategoryService } from '../services/categoryService';
import type { Category, SecondaryCategory } from '../types/category';

interface CreateRewardFormProps {
  cardId: string;
  cardName: string;
  bankName: string;
  bankSlug: string;
  cardSlug: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateRewardForm: React.FC<CreateRewardFormProps> = ({
  cardId,
  cardName,
  bankName,
  bankSlug,
  cardSlug,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    logo: {
      url: '',
      alt: ''
    },
    rewards: {
        type: '',
        value: 0,
        unit: '',
        description: '',
        minSpend: null,
        maxSpend: null,
        currency: 'INR',
        limit: null,
        frequency: null  
    },
    categoryPrimary: '',
    subcategory: '',
    tags: [] as string[],
    status: 'active',
    displayOrder: 1
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState('');

  // Category states
  const [categories, setCategories] = useState<Category[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState<string>('');

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch secondary categories when primary category changes
  useEffect(() => {
    if (selectedPrimaryCategory) {
      fetchSecondaryCategories(selectedPrimaryCategory);
    } else {
      setSecondaryCategories([]);
    }
  }, [selectedPrimaryCategory]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await CategoryService.getPrimaryCategories(true); // Only active categories
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchSecondaryCategories = async (primarySlug: string) => {
    try {
      console.log('Fetching secondary categories for:', primarySlug);
      const response = await CategoryService.getSecondaryCategories(primarySlug, true); // Only active secondary categories
      console.log('Secondary categories response:', response);
      
      // Handle different response structures
      let secondaryData = null;
      if (response.data && Array.isArray(response.data)) {
        secondaryData = response.data;
      } else if (Array.isArray(response)) {
        secondaryData = response;
      } else if (response && typeof response === 'object' && !response.success) {
        // If response is an object but not our expected structure, it might be the data directly
        secondaryData = Array.isArray(response) ? response : [response];
      } else if (response && (response as any).secondaryCategories && Array.isArray((response as any).secondaryCategories)) {
        // Handle case where response has secondaryCategories property
        secondaryData = (response as any).secondaryCategories;
      } else if (response && response.data && (response.data as any).secondaryCategories && Array.isArray((response.data as any).secondaryCategories)) {
        // Handle case where response.data has secondaryCategories property
        secondaryData = (response.data as any).secondaryCategories;
      }
      
      if (secondaryData && Array.isArray(secondaryData)) {
        setSecondaryCategories(secondaryData as unknown as SecondaryCategory[]);
        console.log('Set secondary categories:', secondaryData);
      } else {
        console.log('No secondary categories data or not an array:', response);
        setSecondaryCategories([]);
      }
    } catch (err) {
      console.error('Error fetching secondary categories:', err);
      setError('Failed to load secondary categories');
      setSecondaryCategories([]);
    }
  };

  const handlePrimaryCategoryChange = (categorySlug: string) => {
    setSelectedPrimaryCategory(categorySlug);
    const selectedCategory = categories.find(cat => cat.primarySlug === categorySlug);
    if (selectedCategory) {
      handleInputChange('categoryPrimary', selectedCategory.primaryCategory);
    }
    // Reset subcategory when primary category changes
    handleInputChange('subcategory', '');
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const copy = { ...prev };
      const keys = field.split('.');
      let current: any = copy;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      // Auto-set unit based on type selection
      if (field === 'rewards.type') {
        const unitMap: { [key: string]: string } = {
          'points': 'RPs',
          'discount': '%',
          'cashback': '%',
          'voucher': 'INR'
        };
        if (unitMap[value]) {
          copy.rewards.unit = unitMap[value];
        }
      }
      
      return copy;
    });
  };

  const handleTagsChange = (value: string) => {
    setTagsInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.shortName || !formData.rewards.type) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const rewardData = {
        ...formData,
        bankId: cardId,
        bankName,
        bankSlug,
        cardName,
        cardSlug
      };

      // Import CardService dynamically to avoid circular imports
      const { CardService } = await import('../services/cardService');
      await CardService.createReward(rewardData);
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating reward:', err);
      setError(err instanceof Error ? err.message : 'Failed to create reward');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Create New Reward</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Flipkart Shopping"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Name *</label>
                <input
                  type="text"
                  value={formData.shortName}
                  onChange={(e) => handleInputChange('shortName', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., flipkart_shopping"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Primary *</label>
                <select
                  value={selectedPrimaryCategory}
                  onChange={(e) => handlePrimaryCategoryChange(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={categoriesLoading}
                >
                  <option value="">Select a primary category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.primarySlug}>
                      {category.primaryCategory}
                    </option>
                  ))}
                </select>
                {categoriesLoading && (
                  <p className="mt-1 text-xs text-gray-500">Loading categories...</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => handleInputChange('subcategory', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!selectedPrimaryCategory || secondaryCategories.length === 0}
                >
                  <option value="">Select a subcategory</option>
                  {secondaryCategories.map((subcategory) => (
                    <option key={subcategory._id} value={subcategory.name}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                {selectedPrimaryCategory && secondaryCategories.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No subcategories available for this category</p>
                )}
                {!selectedPrimaryCategory && (
                  <p className="mt-1 text-xs text-gray-500">Please select a primary category first</p>
                )}
              </div>
            </div>
          </div>

          {/* Logo Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Logo Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={formData.logo.url}
                  onChange={(e) => handleInputChange('logo.url', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo Alt Text</label>
                <input
                  type="text"
                  value={formData.logo.alt}
                  onChange={(e) => handleInputChange('logo.alt', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Flipkart Logo"
                />
              </div>
            </div>
          </div>

          {/* Reward Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Reward Details</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={formData.rewards.type}
                  onChange={(e) => handleInputChange('rewards.type', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select type</option>
                  <option value="points">Points</option>
                  <option value="discount">Discount</option>
                  <option value="cashback">Cashback</option>
                  <option value="voucher">Voucher</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  type="number"
                  value={formData.rewards.value}
                  onChange={(e) => handleInputChange('rewards.value', parseFloat(e.target.value) || 0)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <input
                  type="text"
                  value={formData.rewards.unit}
                  onChange={(e) => handleInputChange('rewards.unit', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., %, RPs, INR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.rewards.description}
                  onChange={(e) => handleInputChange('rewards.description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Get 3% cashback on Flipkart purchases"
                />
              </div>
            </div>
          </div>

          {/* Reward Conditions */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Reward Conditions</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Spend</label>
                <input
                  type="number"
                  value={formData.rewards.minSpend ?? ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const nextValue = input === '' ? null : Number(input);
                    handleInputChange('rewards.minSpend', Number.isNaN(nextValue as number) ? null : nextValue);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 1000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Spend</label>
                <input
                  type="number"
                  value={formData.rewards.maxSpend ?? ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    const nextValue = input === '' ? null : Number(input);
                    handleInputChange('rewards.maxSpend', Number.isNaN(nextValue as number) ? null : nextValue);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 20000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <input
                  type="text"
                  value={formData.rewards.currency}
                  onChange={(e) => handleInputChange('rewards.currency', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., INR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
                <input
                  type="number"
                  value={formData.rewards.limit || ''}
                  onChange={(e) => handleInputChange('rewards.limit', parseInt(e.target.value) || null)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 5000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <input
                  type="text"
                  value={formData.rewards.frequency || ''}
                  onChange={(e) => handleInputChange('rewards.frequency', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., monthly, quarterly"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 1)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 6"
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Tags</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => handleTagsChange(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., shopping, online, ecommerce, flipkart"
              />
              {formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Status</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Reward'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRewardForm;
